/**
 * Runtime configuration view (`system.admin`). Dependency-Track is the narrow
 * editable section; the remaining effective settings stay read-only.
 *
 * The endpoint is documented and implemented as SECRET-FREE: it exposes the
 * Dependency-Track API key only as the boolean `dt_api_key_configured`, and the
 * CMDBuild credentials not at all. This panel still masks defensively, because a
 * configuration echo is exactly the endpoint that grows a leak later:
 *
 *   - a secret-looking KEY with a string value is replaced by a mask,
 *   - URL userinfo (`scheme://user:pass@host`, the DATABASE_URL shape) is
 *     stripped, and
 *   - whenever masking actually fires, the panel says so in a warning banner —
 *     a masked value here is a backend finding, not a cosmetic detail.
 *
 * Unknown keys are rendered too (under their raw name): a new setting must not
 * silently disappear from the operator's view just because this build predates
 * it.
 */

import { i18n as globalI18n, type MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import { Banner } from '../../components/Banner';
import { Chip } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchSettings, updateDependencyTrackSettings } from '../../lib/api';
import { DASH } from '../../lib/format';
import type { SettingsResponse } from '../../lib/types';

const MASK = '••••••••';

/** Key names whose STRING values must never be printed. */
const SECRET_KEY_RE = /(pass(word|wort)?|secret|token|credential|api[_-]?key|_key$|^key$)/i;

/** Labels for the known keys, in the order `GET /settings` returns them. */
const SETTING_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  app_version: msg`Version`,
  app_revision: msg`Code-Stand`,
  app_variant: msg`Anwendungsvariante`,
  demo_reset_enabled: msg`Demo-Backups`,
  data_dir: msg`Datenverzeichnis`,
  sbom_dir: msg`SBOM-Verzeichnis`,
  log_dir: msg`Log-Verzeichnis`,
  dt_url: msg`Dependency-Track API-URL`,
  dt_frontend_url: msg`Dependency-Track Weboberfläche`,
  dt_api_key_configured: msg`Dependency-Track API-Key`,
  dt_verify_ssl: msg`Dependency-Track TLS-Prüfung`,
  dt_project_version: msg`Dependency-Track Projektversion`,
  dt_settings_source: msg`Dependency-Track Quelle`,
  cmdbuild_url: msg`CMDBuild URL`,
  cmdbuild_api: msg`CMDBuild API-Pfad`,
  scheduler_enabled: msg`Scheduler`,
  scheduler_cron: msg`Scheduler-Zeitpunkt`,
  kev_reconcile_cron: msg`KEV-Abgleich`,
};

const SETTING_ORDER: readonly string[] = [
  'app_version',
  'app_revision',
  'app_variant',
  'demo_reset_enabled',
  'data_dir',
  'sbom_dir',
  'log_dir',
  'dt_url',
  'dt_frontend_url',
  'dt_api_key_configured',
  'dt_verify_ssl',
  'dt_project_version',
  'dt_settings_source',
  'cmdbuild_url',
  'cmdbuild_api',
  'scheduler_enabled',
  'scheduler_cron',
  'kev_reconcile_cron',
];

/** Build identifiers: no row at all instead of a dash when they are unknown. */
const OMIT_WHEN_EMPTY: ReadonlySet<string> = new Set(['app_version', 'app_revision']);

/** Boolean rendering per key: [true label, false label]. */
const BOOL_LABELS: Readonly<Record<string, readonly [MessageDescriptor, MessageDescriptor]>> = {
  demo_reset_enabled: [msg`freigeschaltet`, msg`gesperrt`],
  dt_api_key_configured: [msg`konfiguriert`, msg`nicht konfiguriert`],
  dt_verify_ssl: [msg`aktiv`, msg`inaktiv`],
  scheduler_enabled: [msg`aktiv`, msg`inaktiv`],
};

/** Replace `scheme://user:pass@host` with `scheme://••••••••@host`. */
function maskUrlCredentials(value: string): { text: string; masked: boolean } {
  const match = /^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)([^/@\s]+)@/.exec(value);
  if (match === null) return { text: value, masked: false };
  const scheme = match[1] ?? '';
  return { text: `${scheme}${MASK}@${value.slice(match[0].length)}`, masked: true };
}

interface SettingRow {
  key: string;
  label: string;
  /** Rendered content of the value cell. */
  value: ReactNode;
  /** True when this page hid something that looked like a secret. */
  masked: boolean;
  /** True when the backend sent a key this build does not know. */
  unknown: boolean;
}

/** Translated [true, false] labels of a boolean setting; yes/no for unknown keys. */
function boolLabels(key: string): readonly [string, string] {
  const labels = BOOL_LABELS[key];
  if (labels === undefined) return [t`ja`, t`nein`];
  return [globalI18n._(labels[0]), globalI18n._(labels[1])];
}

/** Translated at call time (global i18n); the caller re-runs it on a language switch. */
function toRow(key: string, raw: unknown): SettingRow {
  const descriptor = SETTING_LABELS[key];
  const unknown = descriptor === undefined;
  const label = unknown ? key : globalI18n._(descriptor);

  if (typeof raw === 'boolean') {
    // A boolean cannot leak a secret — this is the shape a config echo should use.
    const [onLabel, offLabel] = boolLabels(key);
    return {
      key,
      label,
      value: <Chip tone={raw ? 'good' : 'neutral'}>{raw ? onLabel : offLabel}</Chip>,
      masked: false,
      unknown,
    };
  }

  if (raw === null || raw === undefined || raw === '') {
    return { key, label, value: <span className="muted">{DASH}</span>, masked: false, unknown };
  }

  if (typeof raw === 'number') {
    return { key, label, value: <span className="mono">{String(raw)}</span>, masked: false, unknown };
  }

  if (typeof raw !== 'string') {
    // Objects/arrays: print them, but never guess at their contents.
    return {
      key,
      label,
      value: <span className="mono">{JSON.stringify(raw)}</span>,
      masked: false,
      unknown,
    };
  }

  if (key === 'dt_settings_source') {
    return {
      key,
      label,
      value: raw === 'runtime' ? t`in der Oberfläche gespeichert` : t`Deployment-Umgebung`,
      masked: false,
      unknown,
    };
  }

  if (SECRET_KEY_RE.test(key)) {
    return {
      key,
      label,
      value: (
        <span className="ops-mask" title={t`Maskiert, weil der Wert wie ein Geheimnis aussieht.`}>
          {MASK}
        </span>
      ),
      masked: true,
      unknown,
    };
  }

  const { text, masked } = maskUrlCredentials(raw);
  return { key, label, value: <span className="mono">{text}</span>, masked, unknown };
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.hostname !== '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      parsed.search === '' &&
      parsed.hash === ''
    );
  } catch {
    return false;
  }
}

export function SettingsPanel(): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [reexportRequired, setReexportRequired] = useState(false);
  const savingRef = useRef(false);

  const [dtUrl, setDtUrl] = useState('');
  const [dtFrontendUrl, setDtFrontendUrl] = useState('');
  const [dtApiKey, setDtApiKey] = useState('');
  const [dtVerifySsl, setDtVerifySsl] = useState(true);
  const [dtProjectVersion, setDtProjectVersion] = useState('latest');

  const markEdited = useCallback((): void => {
    setSavedMessage(null);
    setSaveError(null);
  }, []);

  const applySettings = useCallback((next: SettingsResponse): void => {
    setSettings(next);
    setDtUrl(next.dt_url);
    setDtFrontendUrl(next.dt_frontend_url ?? '');
    setDtApiKey('');
    setDtVerifySsl(next.dt_verify_ssl);
    setDtProjectVersion(next.dt_project_version);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      applySettings(await fetchSettings());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo<readonly SettingRow[]>(() => {
    if (settings === null) return [];
    // A Map keeps keys the backend added but this build does not declare.
    const raw = new Map<string, unknown>(Object.entries(settings));
    const extra = [...raw.keys()].filter((key) => !SETTING_ORDER.includes(key));
    return [...SETTING_ORDER, ...extra]
      // Without a version or revision the row is left out rather than shown as unknown.
      .filter((key) => raw.has(key) && !(OMIT_WHEN_EMPTY.has(key) && raw.get(key) === ''))
      .map((key) => toRow(key, raw.get(key)));
    // `locale`: labels are translated inside toRow.
  }, [settings, locale]);

  const maskedRows = rows.filter((row) => row.masked);
  const unknownRows = rows.filter((row) => row.unknown);

  const normalisedFormUrl = dtUrl.trim().replace(/\/+$/, '');
  const normalisedCurrentUrl = settings?.dt_url.trim().replace(/\/+$/, '') ?? '';
  const normalisedFrontendUrl = dtFrontendUrl.trim().replace(/\/+$/, '');
  const normalisedCurrentFrontendUrl = settings?.dt_frontend_url?.trim().replace(/\/+$/, '') ?? '';
  const hasNewKey = dtApiKey.trim() !== '';
  const hasChanges =
    settings !== null &&
    (normalisedFormUrl !== normalisedCurrentUrl ||
      normalisedFrontendUrl !== normalisedCurrentFrontendUrl ||
      hasNewKey ||
      dtVerifySsl !== settings.dt_verify_ssl ||
      dtProjectVersion.trim() !== settings.dt_project_version);

  const urlValid = isHttpUrl(dtUrl);
  const frontendUrlValid = dtFrontendUrl.trim() === '' || isHttpUrl(dtFrontendUrl);
  const apiKeyValid = settings?.dt_api_key_configured === true || hasNewKey;
  const formValid = urlValid && frontendUrlValid && apiKeyValid && dtProjectVersion.trim() !== '';

  const save = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      if (settings === null || !formValid || !hasChanges || savingRef.current) return;
      savingRef.current = true;
      setSaving(true);
      setSaveError(null);
      setSavedMessage(null);
      try {
        const result = await updateDependencyTrackSettings({
          dt_url: dtUrl.trim(),
          dt_frontend_url: dtFrontendUrl.trim(),
          ...(hasNewKey ? { dt_api_key: dtApiKey.trim() } : {}),
          clear_api_key: false,
          dt_verify_ssl: dtVerifySsl,
          dt_project_version: dtProjectVersion.trim(),
        });
        applySettings(result.settings);
        setSavedMessage(
          result.connection_validated
            ? t`Gespeichert und Verbindung zu Dependency-Track erfolgreich geprüft.`
            : t`Dependency-Track Konfiguration gespeichert.`,
        );
        // Keep the migration reminder visible across subsequent saves. A later
        // unrelated change does not make the required portfolio export obsolete.
        setReexportRequired((current) => current || result.reexport_required);
      } catch (err) {
        setSaveError(errorMessage(err));
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [
      applySettings,
      dtApiKey,
      dtFrontendUrl,
      dtProjectVersion,
      dtUrl,
      dtVerifySsl,
      formValid,
      hasChanges,
      hasNewKey,
      settings,
      locale,
    ],
  );

  return (
    <div className="card">
      <div className="card-head">
        <h2><Trans>Konfiguration</Trans></h2>
      </div>
      <div className="card-body">
        {error !== null ? (
          <Banner kind="error" sub={t`Die Konfiguration ist nur mit der Berechtigung system.admin lesbar.`}>
            {error}
          </Banner>
        ) : null}
        {error !== null && settings === null ? (
          <button type="button" className="ghost small" onClick={() => void load()} disabled={loading}>
            {loading ? <Spinner label={t`Versucht erneut…`} /> : t`Erneut versuchen`}
          </button>
        ) : null}

        {maskedRows.length > 0 ? (
          <Banner
            kind="error"
            items={maskedRows.map((row) => `${row.key} (${row.label})`)}
            sub={t`Lassen Sie prüfen, warum der Server diese Werte ausliefert.`}
          >
            <Trans>
              Der Server hat Werte geliefert, die wie Geheimnisse aussehen. Sie werden hier maskiert
              angezeigt.
            </Trans>
          </Banner>
        ) : null}

        {unknownRows.length > 0 ? (
          <Banner kind="warn" items={unknownRows.map((row) => row.key)}>
            <Trans>
              Der Server liefert Einstellungen, die diese Oberfläche noch nicht kennt. Sie werden unten
              unter ihrem technischen Namen angezeigt.
            </Trans>
          </Banner>
        ) : null}

        {loading && settings === null ? (
          <div className="empty">
            <Spinner /> <Trans>Konfiguration wird geladen…</Trans>
          </div>
        ) : settings === null || rows.length === 0 ? (
          <EmptyState title={t`Keine Konfiguration verfügbar`} />
        ) : (
          <>
            <form className="ops-dt-form" onSubmit={(event) => void save(event)}>
              <div className="ops-dt-form-head">
                <h3>Dependency-Track</h3>
                <Chip tone={settings.dt_api_key_configured ? 'good' : 'neutral'}>
                  {settings.dt_api_key_configured ? t`API-Key konfiguriert` : t`API-Key fehlt`}
                </Chip>
              </div>

              {saveError !== null ? <Banner kind="error">{saveError}</Banner> : null}
              {savedMessage !== null ? <Banner kind="ok">{savedMessage}</Banner> : null}
              {reexportRequired ? (
                <Banner kind="warn">
                  <Trans>
                    Server-URL oder Projektversion wurde geändert. Führen Sie den SBOM-Export einmal
                    aus, damit alle Projekte neu zugeordnet werden.
                  </Trans>
                </Banner>
              ) : null}

              <fieldset className="ops-dt-fieldset" disabled={saving}>
                <legend className="visually-hidden"><Trans>Dependency-Track Verbindungsdaten</Trans></legend>
                <div className="ops-dt-fields">
                  <div className="field ops-dt-wide">
                    <label htmlFor="dt-url"><Trans>Dependency-Track API-URL</Trans></label>
                    <input
                      id="dt-url"
                      type="text"
                      inputMode="url"
                      value={dtUrl}
                      onChange={(event) => {
                        markEdited();
                        setDtUrl(event.target.value);
                      }}
                      aria-invalid={!urlValid}
                      aria-describedby="dt-url-hint"
                      placeholder="https://dependency-track.example.com"
                      required
                    />
                    <span id="dt-url-hint" className={!urlValid ? 'err' : 'hint'}>
                      <Trans>HTTP(S)-URL ohne Zugangsdaten, Query oder Fragment.</Trans>
                    </span>
                  </div>

                  <div className="field ops-dt-wide">
                    <label htmlFor="dt-frontend-url"><Trans>Weboberfläche (optional)</Trans></label>
                    <input
                      id="dt-frontend-url"
                      type="text"
                      inputMode="url"
                      value={dtFrontendUrl}
                      maxLength={2048}
                      onChange={(event) => {
                        markEdited();
                        setDtFrontendUrl(event.target.value);
                      }}
                      aria-invalid={!frontendUrlValid}
                      aria-describedby="dt-frontend-url-hint"
                      placeholder="https://dependency-track.example.com"
                    />
                    <span id="dt-frontend-url-hint" className={!frontendUrlValid ? 'err' : 'hint'}>
                      {frontendUrlValid
                        ? t`Adresse, unter der Sie Dependency-Track im Browser öffnen. Ohne Angabe entfallen die direkten CVE-Links im Herstellerimport.`
                        : t`Bitte eine HTTP(S)-Adresse ohne Zugangsdaten, Query oder Fragment eingeben.`}
                    </span>
                  </div>

                  <div className="field ops-dt-wide">
                    <label htmlFor="dt-api-key"><Trans>API-Key</Trans></label>
                    <input
                      id="dt-api-key"
                      type="password"
                      autoComplete="new-password"
                      value={dtApiKey}
                      onChange={(event) => {
                        markEdited();
                        setDtApiKey(event.target.value);
                      }}
                      aria-invalid={!apiKeyValid}
                      aria-describedby="dt-api-key-hint"
                      placeholder={
                        settings.dt_api_key_configured
                          ? t`Leer lassen, um den vorhandenen Key beizubehalten`
                          : t`Dependency-Track API-Key einfügen`
                      }
                    />
                    <span id="dt-api-key-hint" className={!apiKeyValid ? 'err' : 'hint'}>
                      {settings.dt_api_key_configured
                        ? t`Der gespeicherte Key wird nie angezeigt; ein leerer Wert behält ihn bei.`
                        : t`Für Portfolio-Lesezugriff und SBOM-Uploads ist ein API-Key erforderlich.`}
                    </span>
                  </div>

                  <div className="field">
                    <label htmlFor="dt-project-version"><Trans>Projektversion</Trans></label>
                    <input
                      id="dt-project-version"
                      type="text"
                      value={dtProjectVersion}
                      onChange={(event) => {
                        markEdited();
                        setDtProjectVersion(event.target.value);
                      }}
                      aria-invalid={dtProjectVersion.trim() === ''}
                      aria-describedby="dt-project-version-hint"
                      required
                    />
                    <span
                      id="dt-project-version-hint"
                      className={dtProjectVersion.trim() === '' ? 'err' : 'hint'}
                    >
                      {dtProjectVersion.trim() === ''
                        ? t`Die Projektversion ist erforderlich.`
                        : t`Änderungen erfordern anschließend einen neuen SBOM-Export.`}
                    </span>
                  </div>

                  <fieldset className="field ops-dt-checks">
                    <legend className="field-label"><Trans>Verbindung</Trans></legend>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={dtVerifySsl}
                        onChange={(event) => {
                          markEdited();
                          setDtVerifySsl(event.target.checked);
                        }}
                      />
                      <Trans>TLS-Zertifikat prüfen</Trans>
                    </label>
                  </fieldset>
                </div>

                <div className="ops-dt-actions">
                  <span className="muted">
                    {settings.dt_settings_source === 'runtime'
                      ? t`Quelle: diese Oberfläche`
                      : t`Quelle: Deployment-Umgebung`}
                  </span>
                  <button
                    type="submit"
                    className="primary"
                    disabled={!formValid || !hasChanges || saving}
                  >
                    {saving ? (
                      <Spinner variant="on-accent" label={t`Wird gespeichert…`} />
                    ) : (
                      <Trans>Speichern</Trans>
                    )}
                  </button>
                </div>
              </fieldset>
            </form>

            <div className="ops-effective-settings">
              <h3><Trans>Effektive Konfiguration</Trans></h3>
              <dl className="detail-list" style={{ marginBottom: 0 }}>
                {rows.map((row) => (
                  <div key={row.key} style={{ display: 'contents' }}>
                    <dt title={row.key}>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SettingsPanel;
